import _get from 'lodash/get'
import { connect } from 'react-redux'
import React from 'react'
import { withRouter } from 'react-router-dom'

import { FETCH_BRAND } from '../../_common/redux/brands/actions'
import { fetchAPI } from '../../_common/core/http'
import Card from '../_library/Card'
import LoadingBar from '../_library/LoadingBar'
import PermissionDenied from '../_library/PermissionDeniedComponent'
import { getTitle } from '../utils/getTitle'
import { checkCountry } from '../utils/coutriesUtils'

const UNIT_SCRIPT_URL = 'https://ui.unit.co/release/latest/components-extended.js'

@withRouter
@connect(
  state => ({
    brand: state.brands.selected,
    user: state.auth.user,
  }),
  { FETCH_BRAND },
)
export default class BrandCapitalCredit extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      unitScriptLoaded: false,
      unitScriptError: false,
      jwtToken: null,
      tokenLoading: true,
      tokenError: null,
    }
  }

  componentDidMount() {
    const {
      brand: { displayName },
      configs,
    } = this.props

    const configDocTitle = _get(configs, 'messages.documentTitle', '')
    document.title = getTitle(configDocTitle, [displayName])

    this.loadUnitScript()
    this.fetchJwtToken()
  }

  componentWillUnmount() {
    // Cleanup Unit script if needed
    const existingScript = document.querySelector(`script[src="${UNIT_SCRIPT_URL}"]`)
    if (existingScript) {
      existingScript.remove()
    }
  }

  loadUnitScript = () => {
    // Check if script already exists
    const existingScript = document.querySelector(`script[src="${UNIT_SCRIPT_URL}"]`)
    if (existingScript) {
      this.setState({ unitScriptLoaded: true })
      return
    }

    const script = document.createElement('script')
    script.src = UNIT_SCRIPT_URL
    script.async = true
    script.onload = () => {
      this.setState({ unitScriptLoaded: true })
    }
    script.onerror = () => {
      this.setState({ unitScriptError: true })
    }
    document.body.appendChild(script)
  }

  fetchJwtToken = async () => {
    const { brand } = this.props
    const brandId = _get(brand, 'id')
    try {
      const data = await fetchAPI(`/api/brand/${brandId}/jwt_auth`)
      this.setState({
        jwtToken: data.token,
        tokenLoading: false,
      })
    } catch (error) {
      const errorMessage = _get(error, 'errors[0].details', 'Failed to fetch JWT token')
      this.setState({
        tokenError: errorMessage,
        tokenLoading: false,
      })
    }
  }

  canAccessCapitalCredit = () => {
    const { brand } = this.props

    // Check if user is admin or owner
    const isAdminOrOwner = _get(brand, 'isAdmin', false) || _get(brand, 'isOwner', false)

    // Check if brand has flagEnableCapitalLineOfCredit flag enabled
    const hasCapitalFlag = _get(brand, 'flagEnableCapitalLineOfCredit', false)

    // Check if brand is in United States
    const isUSBrand = checkCountry(_get(brand, 'country', ''), 'us')

    return isAdminOrOwner && hasCapitalFlag && isUSBrand
  }

  renderAccessDenied = () => {
    const { brand } = this.props
    const isUSBrand = checkCountry(_get(brand, 'country', ''), 'us')

    let message = 'You do not have access to this feature.'

    if (!isUSBrand) {
      message = 'Line of credit is only available for brands with a United States legal entity.'
    }

    return <PermissionDenied subHeader={message} />
  }

  renderLoading = () => (
    <div className="container-fluid">
      <div className="row">
        <div className="col-xs-12">
          <Card title="Line of Credit">
            <div className="text-center" style={{ padding: '40px' }}>
              <LoadingBar />
              <p style={{ marginTop: '20px' }}>Loading line of credit application...</p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )

  renderError = errorMessage => (
    <div className="container-fluid">
      <div className="row">
        <div className="col-xs-12">
          <Card title="Line of Credit">
            <div className="alert alert-danger" style={{ margin: '20px' }}>
              <strong>Error:</strong> {errorMessage}
            </div>
            <div className="text-center" style={{ marginBottom: '20px' }}>
              <button className="btn btn-primary" onClick={this.fetchJwtToken}>
                Retry
              </button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )

  renderUnitComponent = () => {
    const { jwtToken } = this.state

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-xs-12">
            <Card title="Line of Credit" icon="fa-credit-card">
              <div className="card-block">
                <div className="unit-capital-container" style={{ minHeight: '500px' }}>
                  <unit-elements-white-label-app jwt-token={jwtToken} />
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    )
  }

  render() {
    const { unitScriptLoaded, unitScriptError, tokenLoading, tokenError, jwtToken } = this.state

    // Check access permissions first
    if (!this.canAccessCapitalCredit()) {
      return this.renderAccessDenied()
    }

    // Show error if Unit script failed to load
    if (unitScriptError) {
      return this.renderError('Failed to load the line of credit service. Please try again later.')
    }

    // Show loading while fetching token or loading script
    if (tokenLoading || !unitScriptLoaded) {
      return this.renderLoading()
    }

    // Show error if token fetch failed
    if (tokenError) {
      return this.renderError(tokenError)
    }

    // Show error if no token available
    if (!jwtToken) {
      return this.renderError('Unable to initialize line of credit service. Please contact support.')
    }

    // Render the Unit component
    return this.renderUnitComponent()
  }
}
